import React from "react";
import { Link } from "react-router-dom";
import { BlogImage } from "./blog-image";
import { BlogMeta } from "./blog-meta";
import { CategoryBadge } from "./category-badge";

/**
 * @typedef {Object} BlogCardProps
 * @property {PostResponse} post
 * @property {boolean} featured
 */

/**
 * @param {BlogCardProps} props
 * @returns {React.ReactNode}
 */
export const BlogCard = ({ post, featured = false }) => {
  const formattedDate = new Date(post.createdAt).toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  // Remove underline from all Link children via Tailwind 'no-underline'
  if (featured) {
    return (
      <div className="animate-fade-in">
        <div className="group max-w-full overflow-hidden bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 border border-gray-200/60 hover:border-primary/20">
          <Link
            to={`/blog/${post.slug}`}
            className="grid grid-cols-1 md:grid-cols-2 gap-0 no-underline"
          >
            <BlogImage
              src={post.featuredImage}
              alt={post.title}
              containerClassName="h-64 md:h-full rounded-t-3xl md:rounded-l-3xl md:rounded-tr-none"
              showOverlay
            />
            <div className="p-8 md:p-10 flex flex-col justify-center bg-gradient-to-br from-white via-gray-50/50 to-white">
              <CategoryBadge category={post.category} className="w-max" />
              <h2 className="font-bold text-2xl md:text-3xl lg:text-4xl mt-5 group-hover:text-primary transition-colors duration-300 leading-tight text-gray-900">
                {post.title}
              </h2>
              {post.excerpt && (
                <p className="mt-5 text-gray-600 line-clamp-2 md:line-clamp-3 leading-relaxed text-base">
                  {post.excerpt}
                </p>
              )}
              <BlogMeta
                date={formattedDate}
                readTime={post.readTime}
                className="mt-7"
                iconSize={16}
              />
            </div>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <article className="group bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-500 border border-gray-200/60 hover:border-primary/20 overflow-hidden animate-fade-up hover:-translate-y-1">
      <Link to={`/blog/${post.slug}`} className="block no-underline">
        <BlogImage
          src={post.featuredImage}
          alt={post.title}
          containerClassName="h-48"
          showOverlay
        />
        <div className="p-6 bg-gradient-to-br from-white to-gray-50/30">
          <CategoryBadge category={post.category} />
          <h3 className="font-bold text-lg mt-4 group-hover:text-primary transition-colors duration-300 line-clamp-2 leading-tight text-gray-900">
            {post.title}
          </h3>
          <BlogMeta
            date={formattedDate}
            readTime={post.readTime}
            className="mt-5"
            iconSize={14}
          />
        </div>
      </Link>
    </article>
  );
};
